/**
 * Item de cardápio no PADRÃO iFOOD (Catalog v2) — o único formato de
 * `merchants/{id}/cardapio/{itemId}`.
 *
 * O espelho do iFood (backend, menuSyncService) já grava assim; os itens criados no
 * Auuii (adicionar, editar, publicar do estoque) gravavam só em português, e cada
 * integração precisava adivinhar os dois formatos. Agora todo item, venha de onde
 * vier, tem os mesmos campos:
 *
 *   id / itemId / productId / externalCode
 *   name, description, imageUrl, imagePath
 *   price            número em reais (com tamanhos: o menor preço, "a partir de")
 *   status           'AVAILABLE' | 'UNAVAILABLE'
 *   categoryId, categoryName
 *   template         'DEFAULT' | 'PIZZA' | 'COMBO' — o modelo do item (ver utils/opcoesItem.js)
 *   optionGroups     tamanhos, sabores, borda, etapas de combo e complementos (iFood);
 *                    na marmitex, tamanhos P/M/G = grupo "Tamanho" (min 1, max 1)
 *   shifts           dias/horários = shifts do iFood ({ startTime, endTime, monday… })
 *   source           'auuii' | 'ifood'
 *
 * Os campos em português (nome, descricao, preco, precos, tipo, disponivel,
 * diasSelecionados) continuam sendo gravados como APELIDOS: cardápio do cliente,
 * carrinho, marmitex e baixa de estoque ainda leem eles. Quem lê item novo deve usar
 * `lerItemCardapio`, que devolve os dois lados preenchidos para qualquer documento.
 *
 * Cópia CommonJS no backend: Auuii-Backend/src/services/cardapioPadrao.js — as duas
 * precisam mudar juntas.
 */

import { MODELOS, modeloDaCategoria, modeloDoItem, normalizarGrupos, precoAPartirDe } from './opcoesItem';

export const CATEGORIAS_AUUII = {
    lanche: 'Lanches',
    bebida: 'Bebidas',
    marmitex: 'Marmitex',
    mercado: 'Mercado',
    pizza: 'Pizzas',
    combo: 'Combos',
    outro: 'Outros',
};

const DIAS = [
    ['segunda', 'monday'], ['terca', 'tuesday'], ['quarta', 'wednesday'], ['quinta', 'thursday'],
    ['sexta', 'friday'], ['sabado', 'saturday'], ['domingo', 'sunday'],
];

const TAMANHOS = { P: 'Pequeno', M: 'Médio', G: 'Grande' };

const numero = (v) => {
    const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
    return Number.isFinite(n) ? n : null;
};

const categoriaIdDoTipo = (tipo) => `auuii-${tipo}`;

/** Tipo Auuii (lanche, bebida…) a partir de um item em qualquer formato. */
export function tipoDoItem(raw = {}) {
    if (raw.tipo) return String(raw.tipo).toLowerCase();
    const catId = String(raw.categoryId || '');
    if (catId.startsWith('auuii-')) return catId.slice(6);
    const cat = String(raw.categoryName || '').toLowerCase();
    if (/bebid|drink|refri|suco/.test(cat)) return 'bebida';
    if (/marmit/.test(cat)) return 'marmitex';
    if (/pizza/.test(cat)) return 'pizza';
    if (/lanche|burg|sandu/.test(cat)) return 'lanche';
    if (/mercado|mercearia/.test(cat)) return 'mercado';
    return 'outro';
}

/** Veio do iFood? (espelho novo grava source; importações antigas só deixaram a categoria do iFood) */
export function origemDoItem(raw = {}) {
    if (raw.source) return raw.source;
    const catId = raw.categoryId ? String(raw.categoryId) : '';
    if ((catId && !catId.startsWith('auuii-')) || raw.ifoodMerchantId || raw.catalogId) return 'ifood';
    return 'auuii';
}

/** diasSelecionados { segunda: { selecionado, inicio, fim } } → shifts do iFood. */
function shiftsDosDias(diasSelecionados) {
    if (!diasSelecionados) return [];
    const shifts = [];
    DIAS.forEach(([pt, en]) => {
        const d = diasSelecionados[pt];
        const ligado = typeof d === 'boolean' ? d : d?.selecionado;
        if (!ligado) return;
        const shift = {
            startTime: (typeof d === 'object' && d.inicio) || '00:00',
            endTime: (typeof d === 'object' && d.fim) || '23:59',
        };
        DIAS.forEach(([, en2]) => { shift[en2] = en2 === en; });
        shifts.push(shift);
    });
    return shifts;
}

/** shifts do iFood → diasSelecionados (para item que veio só no formato iFood). */
function diasDosShifts(shifts) {
    if (!Array.isArray(shifts) || shifts.length === 0) return null;
    const dias = {};
    DIAS.forEach(([pt, en]) => {
        const s = shifts.find((x) => x?.[en]);
        dias[pt] = s
            ? { selecionado: true, inicio: s.startTime || '', fim: s.endTime || '' }
            : { selecionado: false, inicio: '', fim: '' };
    });
    return dias;
}

/** precos { P, M, G } → grupo de opções "Tamanho" do iFood. */
function grupoDeTamanhos(precos, status) {
    const options = Object.entries(precos || {})
        .map(([tam, v]) => ({ tam, v: numero(v) }))
        .filter(({ v }) => v !== null && v > 0)
        .map(({ tam, v }) => ({
            id: `tamanho-${tam}`,
            name: TAMANHOS[tam] || tam,
            externalCode: tam,
            price: v,
            status,
        }));
    if (options.length === 0) return null;
    return { id: 'tamanho', name: 'Tamanho', min: 1, max: 1, status, options };
}

/**
 * Monta o documento para GRAVAR (criar ou editar). Recebe o que a tela tem em mãos —
 * os nomes em português do formulário — e devolve o item no padrão iFood com os
 * apelidos. Campos que a tela não mexe (estoque, imagem) só entram se vierem.
 *
 * @param {object} dados  { nome, descricao, tipo, preco, precos, diasSelecionados,
 *                          disponivel, imageUrl, imagePath, controlaEstoque,
 *                          estoqueItens, origemEstoque, template, optionGroups }
 *                          template: modelo do item (padrão: o da categoria);
 *                          optionGroups: grupos do editor (pizza, combo, complementos)
 * @param {string} [id]   id do documento (também vira itemId/productId/externalCode)
 */
export function montarItemCardapio(dados, id) {
    const tipo = String(dados.tipo || 'outro').toLowerCase();
    const disponivel = dados.disponivel !== false;
    const status = disponivel ? 'AVAILABLE' : 'UNAVAILABLE';
    const nome = String(dados.nome || '').trim();
    const descricao = String(dados.descricao || '').trim();

    const temTamanhos = tipo === 'marmitex' && dados.precos;
    const precos = temTamanhos
        ? Object.fromEntries(Object.entries(dados.precos).map(([k, v]) => [k, numero(v)]))
        : null;
    const grupo = temTamanhos ? grupoDeTamanhos(precos, status) : null;
    const grupos = temTamanhos ? (grupo ? [grupo] : []) : normalizarGrupos(dados.optionGroups);
    // Pizza só existe com tamanhos: item que entra na categoria pizza sem montagem
    // (publicar do estoque, por exemplo) continua simples, com o preço informado.
    const pedido = temTamanhos ? MODELOS.DEFAULT : modeloDoItem({ template: dados.template || modeloDaCategoria(tipo) });
    const template = pedido === MODELOS.PIZZA && !grupos.some((g) => g.type === 'SIZE') ? MODELOS.DEFAULT : pedido;
    // Pizza não tem preço próprio: `price` é o "a partir de" (menor tamanho + sabor mais barato).
    const precoBase = template === MODELOS.PIZZA ? 0 : numero(dados.preco);
    const preco = temTamanhos
        ? (grupo ? Math.min(...grupo.options.map((o) => o.price)) : null)
        : (template === MODELOS.PIZZA ? precoAPartirDe({ template, price: 0, optionGroups: grupos }) : precoBase);

    const item = {
        // ── padrão iFood ──
        name: nome,
        description: descricao,
        price: preco,
        status,
        categoryId: categoriaIdDoTipo(tipo),
        categoryName: CATEGORIAS_AUUII[tipo] || 'Outros',
        template,
        optionGroups: grupos,
        shifts: tipo === 'marmitex' ? shiftsDosDias(dados.diasSelecionados) : [],
        source: 'auuii',

        // ── apelidos em português (leitores antigos) ──
        nome,
        descricao,
        tipo,
        disponivel,
    };

    if (temTamanhos) {
        item.precos = precos;
        item.diasSelecionados = dados.diasSelecionados || {};
    } else {
        item.preco = preco;
    }

    if (id) {
        item.id = id;
        item.itemId = id;
        item.productId = id;
        item.externalCode = id;
    }

    ['imageUrl', 'imagePath', 'controlaEstoque', 'estoqueItens', 'origemEstoque', 'quantidade', 'estoque', 'quantidadeEstoque'].forEach((k) => {
        if (dados[k] !== undefined) item[k] = dados[k];
    });

    return item;
}

/**
 * Lê um item em QUALQUER formato (Auuii antigo, espelho iFood, padrão novo) e devolve
 * os dois lados preenchidos. Não grava nada.
 */
export function lerItemCardapio(raw = {}, docId) {
    const id = raw.id || raw.itemId || docId;
    const tipo = tipoDoItem(raw);
    const status = raw.status || (raw.disponivel === false ? 'UNAVAILABLE' : 'AVAILABLE');
    const disponivel = raw.disponivel ?? status === 'AVAILABLE';

    let precos = raw.precos || null;
    if (!precos && Array.isArray(raw.optionGroups)) {
        const grupo = raw.optionGroups.find((g) => g?.id === 'tamanho' && Array.isArray(g.options));
        if (grupo) {
            precos = Object.fromEntries(grupo.options.map((o) => [o.externalCode || o.name, numero(o.price?.value ?? o.price)]));
        }
    }

    const preco = numero(raw.preco ?? raw.price?.value ?? raw.price ?? raw.originalPreco);
    const nome = raw.nome || raw.name || '';
    const descricao = raw.descricao ?? raw.description ?? '';
    const diasSelecionados = raw.diasSelecionados || diasDosShifts(raw.shifts);

    return {
        ...raw,
        id,
        itemId: raw.itemId || id,
        name: raw.name || nome,
        description: raw.description ?? descricao,
        price: preco,
        status,
        categoryId: raw.categoryId || categoriaIdDoTipo(tipo),
        categoryName: raw.categoryName || CATEGORIAS_AUUII[tipo] || 'Outros',
        template: precos ? MODELOS.DEFAULT : modeloDoItem(raw),
        optionGroups: raw.optionGroups || [],
        source: origemDoItem(raw),
        nome,
        descricao: descricao || '',
        tipo,
        disponivel,
        preco,
        ...(precos ? { precos } : {}),
        ...(diasSelecionados ? { diasSelecionados } : {}),
    };
}
